/**
 * Hard limits and defaults shared by every layer.
 *
 * A wish is a single sentence. There are deliberately no "caveats" or extra
 * conditions: the player writes one wish at the start of the round and nothing
 * else. Everything after that belongs to the other players.
 *
 * Both limits are 35 code points, and that tightness is the point: a wish and
 * the curse that ruins it are one short line each, read at a glance across a
 * room. Any layout built for long paragraphs is the wrong layout.
 *
 * Do NOT "restore" these to the original brief's 100 and 120. The founder
 * shortened them deliberately on 2026-08-22, and an automated run has already
 * reverted them once by treating the original brief as the source of truth.
 */

export const WISH_MAX_LENGTH = 35;
export const CONSEQUENCE_MAX_LENGTH = 35;
export const NICKNAME_MAX_LENGTH = 16;
export const ROOM_NAME_MAX_LENGTH = 32;

export const MIN_PARTICIPANTS_TO_START = 4;
export const MAX_PARTICIPANTS = 10;
/** Below this many humans we offer to fill the room with Devil Bots. */
export const RECOMMENDED_MIN_HUMANS = 4;

export const ROOM_CODE_LENGTH = 5;
/**
 * Room-code alphabet with every visually ambiguous glyph removed:
 * no I/1, no O/0, no S/5, no B/8, no Z/2, no U (reads as V on some TV fonts).
 */
export const ROOM_CODE_ALPHABET = 'ACDEFGHJKLMNPQRTVWXY';

export const PHASES = [
  'LOBBY',
  'ROUND_COUNTDOWN',
  'WISH_INPUT',
  'CONSEQUENCE_INPUT',
  'VOTE_PREVIEW',
  'VOTE_OPEN',
  'REVEAL',
  'SCOREBOARD',
  'GAME_OVER',
] as const;
export type Phase = (typeof PHASES)[number];

/** Fixed, server-authoritative inter-round countdown. */
export const ROUND_COUNTDOWN_SECONDS = 3;

export const PARTICIPANT_KINDS = ['human', 'bot'] as const;
export type ParticipantKind = (typeof PARTICIPANT_KINDS)[number];

export const CONNECTION_ROLES = ['player', 'controller', 'shared-display', 'spectator'] as const;
export type ConnectionRole = (typeof CONNECTION_ROLES)[number];

export const SUBMISSION_STATUSES = ['pending', 'submitted', 'missed'] as const;
export type SubmissionStatus = (typeof SUBMISSION_STATUSES)[number];

export const LOCALES = ['pt-PT', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

/** Every mutating action resolves to exactly one of these. */
export const ACK_STATUSES = [
  'accepted',
  'duplicate',
  'stale-phase',
  'invalid',
  'forbidden',
  'rate-limited',
] as const;
export type AckStatus = (typeof ACK_STATUSES)[number];

export const DEFAULT_SETTINGS = {
  roomName: '',
  locale: 'pt-PT' as Locale,
  rounds: 1,
  wishSeconds: 30,
  consequenceSeconds: 20,
  voteSeconds: 20,
  revealSecondsPerWish: 8,
  /** Hell Mode: no reading window, the vote clock starts with the cards. */
  hellMode: false,
  /** Rejects submissions that trip the family filter instead of rewriting them. */
  familyFilter: false,
  autoReveal: true,
  sharedDisplay: false,
} as const;

/**
 * Reading window before a vote opens, scaled to how many cards must be read.
 * clamp(6 + (answerCount - 3) * 1.25, 6, 14) seconds, rounded to the second.
 */
export function votePreviewSeconds(answerCount: number, hellMode = false): number {
  if (hellMode) return 0;
  const raw = 6 + (answerCount - 3) * 1.25;
  return Math.round(Math.min(14, Math.max(6, raw)));
}
